import { supabase } from "../supabaseClient";
import { v4 as uuidv4 } from "uuid";

/**
 * Fetch active projects
 */
export const getActiveProjects = async () => {
  const { data, error } = await supabase
    .from("Projects")
    .select(
      `
    *
    
    `,
    )
    .eq("isVJ", true)
    .order("projectName", { ascending: true });

  if (error) throw error;
  return data;
};

/**
 * Convert date → epoch (BIGINT)
 */
const toEpoch = (date) => {
  if (!date) return null;
  return new Date(date).getTime();
};

/**
 * Create base brokerage plan
 * ALIGNED WITH DB TYPES:
 * bigint, uuid, numeric, enums
 */
export const createBaseBrokeragePlan = async (formData) => {
  try {
    const {
      planName,
      calcType,
      value,
      startDate,
      endDate,
      isActive,
      scopeType,
      scopeMode,
      selectedProject,
      selectedProjects,
      projectId,
      wingIds = [],
    } = formData;

    const planId = uuidv4();
    const nowEpoch = Date.now();

    // ENUM safety
    const calcTypeEnum = calcType === "Percentage" ? "Percentage" : "Fixed";

    const statusEnum = isActive ? "Active" : "Inactive";

    // 1️⃣ INSERT PLAN
    const { error: planError } = await supabase
      .from("base_brokerage_plans")
      .insert({
        id: planId, // uuid
        name: planName, // text
        calc_type: calcTypeEnum, // enum
        value: Number(value), // numeric
        start_date: toEpoch(startDate), // bigint
        end_date: toEpoch(endDate), // bigint
        status: statusEnum, // enum
        created_at: nowEpoch, // bigint
        updated_at: nowEpoch, // bigint
      });

    if (planError) throw planError;

    let mappings = [];

    // ✅ PROJECT BASED
    if (scopeType === "project") {
      const projectIds =
        scopeMode === "single"
          ? [selectedProject]
          : selectedProjects;

      mappings = projectIds.map((projectId) => ({
        id: uuidv4(),
        base_plan_id: planId,
        project_id: projectId,
        wing_id: null,
        created_at: nowEpoch,
      }));
    }

    // ✅ WING BASED
    if (scopeType === "wing") {
      mappings = [];

      formData.selectedProjectWings.forEach((proj) => {
        proj.wingIds.forEach((wingId) => {
          mappings.push({
            id: uuidv4(),
            base_plan_id: planId,
            project_id: proj.projectId,
            wing_id: wingId,
            created_at: nowEpoch,
          });
        });
      });
    }

    // INSERT
    if (mappings.length > 0) {
      const { error: mappingError } = await supabase
        .from("base_brokerage_plan_projects")
        .insert(mappings);

      if (mappingError) throw mappingError;
    }

    return { success: true, planId };
  } catch (error) {
    console.error("Create Brokerage Plan Error:", error);
    return { success: false, error };
  }
};

/**
 * Fetch plans for listing screen
 */
export const getBaseBrokeragePlans = async () => {
  const { data, error } = await supabase
    .from("base_brokerage_plans")
    .select(
      `
      id,
      name,
      calc_type,
      value,
      start_date,
      end_date,
      status,
      created_at
    `,
    )
    .is("deleted_at", null)
    .order("created_at", { ascending: false });

  if (error) throw error;

  const formattedData = data.map((item) => ({
    ...item,
    start_date: item.start_date
      ? new Date(Number(item.start_date)).toLocaleDateString("en-GB", {
        day: "2-digit",
        month: "short",
        year: "numeric",
      })
      : null,
    end_date: item.end_date
      ? new Date(Number(item.end_date)).toLocaleDateString("en-GB", {
        day: "2-digit",
        month: "short",
        year: "numeric",
      })
      : null,
    created_at: item.created_at
      ? new Date(Number(item.created_at)).toLocaleDateString("en-GB", {
        day: "2-digit",
        month: "short",
        year: "numeric",
      })
      : null,
  }));

  return formattedData;
};

// DELETE PLAN (soft delete)
export const deleteBaseBrokeragePlan = async (planId) => {
  await supabase
    .from("base_brokerage_plan_projects")
    .delete()
    .eq("base_plan_id", planId);

  const { error } = await supabase
    .from("base_brokerage_plans")
    .delete()
    .eq("id", planId);

  if (error) throw error;

  return { success: true };
};

// UPDATE STATUS
export const toggleBrokerageStatus = async (planId, status) => {
  const { error } = await supabase
    .from("base_brokerage_plans")
    .update({
      status: status === "Active" ? "Inactive" : "Active",
      updated_at: Date.now(),
    })
    .eq("id", planId);

  if (error) throw error;
};

/**
 * Applicable Basic Brokerage % for a project (optionally scoped to a wing).
 * Used when creating a brand-new brokerage line for a CP that has no prior
 * CpPayments entry yet (Lead Source Change → Direct→CP / CP→CP new side).
 * Prefers a wing-scoped plan over a project-level one when both are active.
 */
export const getApplicableBasicBrokeragePercentage = async (projectId, wingId = null) => {
  try {
    if (!projectId) return null;

    const { data, error } = await supabase
      .from("base_brokerage_plan_projects")
      .select(`
        project_id,
        wing_id,
        base_brokerage_plans!inner (
          calc_type,
          value,
          status
        )
      `)
      .eq("project_id", projectId)
      .eq("base_brokerage_plans.status", "Active");

    if (error) throw error;

    const rows = data || [];
    const wingRow = wingId ? rows.find((r) => r.wing_id === wingId) : null;
    const projectRow = rows.find((r) => r.wing_id === null);
    const match = wingRow || projectRow;

    if (!match || match.base_brokerage_plans?.calc_type !== "Percentage") {
      return null;
    }

    return Number(match.base_brokerage_plans.value) || null;
  } catch (err) {
    console.error("Get Applicable Basic Brokerage % Error:", err);
    return null;
  }
};

/**
 * Fetch CP list (for POP Step 1 selection)
 * Only active CPs
 */
export const getCPList = async () => {
  try {
    const { data, error } = await supabase
      .from("CP")
      .select(
        `
        cpId,
        billingName
      `,
      )

      .order("billingName", { ascending: true });

    if (error) throw error;

    return data;
  } catch (err) {
    console.error("Fetch CP List Error:", err);
    return [];
  }
};

/**
 * Fetch wings for selected project (POP Step 3)
 */
export const getWingsByProject = async (projectId) => {
  try {
    const { data, error } = await supabase
      .from("Wings")
      .select(
        `
        wingId,
        wingName,
        projectId
      `,
      )
      .eq("projectId", projectId)
      .order("wingName", { ascending: true });

    if (error) throw error;

    return data;
  } catch (err) {
    console.error("Fetch Wings Error:", err);
    return [];
  }
};

/**
 * Fetch units based on selected wings (POP Step 3)
 */
export const getUnitsByWings = async (wingIds) => {
  try {
    const { data, error } = await supabase
      .from("Inventory")
      .select(
        `
        unitId,
        unitNo,
        wingId,
        projectId,
        floorNo,
        inventoryTypeId
      `,
      )
      .in("wingId", wingIds)
      .order("unitNo", { ascending: true });

    if (error) throw error;

    return data;
  } catch (err) {
    console.error("Fetch Units Error:", err);
    return [];
  }
};
export const getInventoryTypes = async (inventoryTypeIds) => {
  try {
    const { data, error } = await supabase
      .from("InventoryType")
      .select("inventoryTypeId, type")
      .in("inventoryTypeId", inventoryTypeIds); // Querying for multiple inventoryTypeIds

    if (error) throw error;

    return data;
  } catch (err) {
    console.error("Fetch Inventory Types Error:", err);
    return [];
  }
};

const formatDate = (date) => {
  if (!date) return null;
  return new Date(date).toISOString().split("T")[0]; // YYYY-MM-DD
};

// Function to insert POP plan data
// Function to insert POP plan data
// Function to insert POP plan data
// Function to insert POP plan data
export const createPopPlan = async (formData) => {
  try {
    const {
      planName,
      scopeType,
      startDate,
      endDate,
      calcType,
      rewardType,
      conflictStrategy,
      isActive,
      selectedChannelPartners,
      rangeRules,
      eligibility,
      projectId,
      wingIds = [],
      unitIds = [],
      file,
    } = formData;

    console.log("Creating POP Plan with data:", formData);

    const popPlanId = uuidv4();
    const now = Date.now();

    /* ENUMS */

    const scopeTypeEnum =
      scopeType === "project"
        ? "Project"
        : scopeType === "wing"
          ? "Wing"
          : "Unit";

    const calcTypeEnum = calcType === "Percentage" ? "Percentage" : "Count";

    const rewardTypeEnum = rewardType === "percentage" ? "Percentage" : "Fixed";

    const conflictStrategyEnum =
      conflictStrategy === "allowBoth" ? "POP_AND_AOP" : "POP_ONLY";

    const statusEnum = isActive ? "Active" : "Inactive";

    /* ✅ UPLOAD FIRST */

    let docUrl = null;

    if (file) {
      const fileExt = file.name.split(".").pop();
      const filePath = `Sm_Uploaded_Docs/${popPlanId}.${fileExt}`;

      const { error: uploadError } = await supabase.storage
        .from("AOP_POP_Documents")
        .upload(filePath, file, { upsert: true });

      if (uploadError) throw uploadError;

      const publicUrlResponse = supabase.storage
        .from("AOP_POP_Documents")
        .getPublicUrl(filePath);

      console.log("PUBLIC URL RESPONSE:", publicUrlResponse);

      docUrl = publicUrlResponse?.data?.publicURL || null;
    }

    console.log("FINAL DOC URL:", docUrl);

    /* ✅ INSERT PLAN WITH DOCURL */

    const { error: planError } = await supabase.from("pop_plans").insert({
      id: popPlanId,
      name: planName,
      scope_type: scopeTypeEnum,
      start_date: toEpoch(startDate),
      end_date: toEpoch(endDate),
      calculation_basis: calcTypeEnum,
      reward_type: rewardTypeEnum,
      conflict_mode: conflictStrategyEnum,
      status: statusEnum,
      created_at: now,
      updated_at: now,
      docurl: docUrl, // 🔥 THIS IS THE KEY
    });

    if (planError) throw planError;

    /* ================== 3️⃣ SCOPE ENTITIES ================== */

    let scopeEntities = [];

    if (scopeType === "project" && projectId) {
      scopeEntities.push({
        id: uuidv4(),
        pop_plan_id: popPlanId,
        project_id: projectId,
        wing_id: null,
        unit_id: null,
        created_at: now,
      });
    }

    if (scopeType === "wing" && projectId && wingIds.length > 0) {
      scopeEntities = wingIds.map((wingId) => ({
        id: uuidv4(),
        pop_plan_id: popPlanId,
        project_id: projectId,
        wing_id: wingId,
        unit_id: null,
        created_at: now,
      }));
    }

    if (scopeType === "unit" && projectId && unitIds.length > 0) {
      scopeEntities = unitIds.map((unitId) => ({
        id: uuidv4(),
        pop_plan_id: popPlanId,
        project_id: projectId,
        wing_id: wingIds?.[0] ?? null,
        unit_id: unitId,
        created_at: now,
      }));
    }

    if (scopeEntities.length === 0) {
      throw new Error("Invalid scope selection");
    }

    const { error: scopeError } = await supabase
      .from("pop_scope_entities")
      .insert(scopeEntities);

    if (scopeError) throw scopeError;

    /* ================== 4️⃣ SLABS ================== */

    if (rangeRules?.length > 0) {
      const slabsPayload = rangeRules.map((rule, index) => ({
        id: uuidv4(),
        pop_plan_id: popPlanId,
        from_value: Number(rule.from),
        to_value: rule.to ? Number(rule.to) : null,
        percentage:
          rewardTypeEnum === "Percentage" ? Number(rule.amount) : null,
        amount: rewardTypeEnum === "Fixed" ? Number(rule.amount) : null,
        sort_order: index + 1,
        created_at: now,
      }));

      const { error: slabError } = await supabase
        .from("pop_slabs")
        .insert(slabsPayload);

      if (slabError) throw slabError;
    }

    /* ================== 5️⃣ ELIGIBILITY ================== */

    const eligibilityPayload = {
      id: uuidv4(),
      pop_plan_id: popPlanId,
      site_visit_enabled: !!eligibility?.siteVisitEnabled,
      min_site_visits: eligibility?.siteVisitMin
        ? Number(eligibility.siteVisitMin)
        : null,
      max_site_visits: eligibility?.siteVisitMax
        ? Number(eligibility.siteVisitMax)
        : null,
      fos_enabled: !!eligibility?.fosEnabled,
      min_fos_count: eligibility?.fosMin ? Number(eligibility.fosMin) : null,
      max_fos_count: eligibility?.fosMax ? Number(eligibility.fosMax) : null,
      created_at: now,
      updated_at: now,
    };

    const { error: eligibilityError } = await supabase
      .from("pop_eligibility_rules")
      .insert(eligibilityPayload);

    if (eligibilityError) throw eligibilityError;

    /* ================== 6️⃣ CP TARGETS ================== */

    if (selectedChannelPartners?.length > 0) {
      const cpPayload = selectedChannelPartners.map((cpId) => ({
        id: uuidv4(),
        pop_plan_id: popPlanId,
        cp_id: cpId,
      }));

      const { error: cpError } = await supabase
        .from("pop_cp_targets")
        .insert(cpPayload);

      if (cpError) throw cpError;
    }

    return { success: true, popPlanId };
  } catch (error) {
    console.error("Create POP Plan Error:", error);
    return { success: false, error };
  }
};

export const checkDuplicatePlanName = async (planName) => {
  const { data, error } = await supabase
    .from("pop_plans")
    .select("id")
    .ilike("name", planName.trim()) // Case-insensitive match
    .maybeSingle();

  if (error) throw error;

  return data; // Returns null if no duplicate found, or returns the existing plan if found
};

/**
 * Create AOP - Step 1 (Financial Year & Status)
 */
export const createAopPlan = async (formData) => {
  try {
    const {
      financialYear,
      isActive,
      validityStartDate,
      validityEndDate,
      signOffRequired,
      signOffStartDate,
      signOffEndDate,
      targetBasis,
      incentiveType,
      targetSlabs = [],
      scopeType,
      excludedProjects = [],
      eligibility,
      file,
      cpScopeType = "ALL",
      selectedCPs = [],
    } = formData;

    console.log("Creating AOP Plan with data:", formData);

    const aopPlanId = uuidv4();
    const nowEpoch = Date.now();

    /* ---------- VALIDATION ---------- */

    if (!financialYear) {
      throw new Error("Financial year is required");
    }

    if (!validityStartDate || !validityEndDate) {
      throw new Error("Validity period is required");
    }

    if (signOffRequired && (!signOffStartDate || !signOffEndDate)) {
      throw new Error("Sign-off dates are required");
    }

    if (cpScopeType === "SPECIFIC" && (!selectedCPs || selectedCPs.length === 0)) {
      throw new Error("Please select at least one CP");
    }

    /* ---------- OVERLAP CHECK ---------- */

    const overlapCheck = await checkAopCpOverlap({
      startDate: validityStartDate,
      endDate: validityEndDate,
      cpScopeType,
      selectedCPs,
    });

    if (overlapCheck.blocked) {
      throw new Error(overlapCheck.message || "AOP overlap found");
    }

    /* ---------- FILE UPLOAD ---------- */

    let docUrl = null;

    if (file) {
      const fileExt = file.name.split(".").pop();
      const filePath = `Sm_Uploaded_Docs/${aopPlanId}.${fileExt}`;

      const { error: uploadError } = await supabase.storage
        .from("AOP_POP_Documents")
        .upload(filePath, file, { upsert: true });

      if (uploadError) throw uploadError;

      const publicUrlResponse = supabase.storage
        .from("AOP_POP_Documents")
        .getPublicUrl(filePath);

      docUrl = publicUrlResponse?.data?.publicURL || null;
    }

    /* ---------- INSERT AOP PLAN ---------- */

    const { error: planError } = await supabase.from("aop_plans").insert({
      id: aopPlanId,
      financial_year: financialYear,
      status: isActive ? "Active" : "Inactive",

      validity_start_date: new Date(validityStartDate).getTime(),
      validity_end_date: new Date(validityEndDate).getTime(),

      accrual_basis: "AGREEMENT_DATE",
      calculation_basis: targetBasis === "revenue" ? "VALUE" : "COUNT",
      reward_type: incentiveType === "percentage" ? "PERCENTAGE" : "FIXED",

      cp_scope_type: cpScopeType, // ✅ NEW

      priority: 0,

      sign_off_start_date: signOffRequired
        ? new Date(signOffStartDate).getTime()
        : null,

      sign_off_end_date: signOffRequired
        ? new Date(signOffEndDate).getTime()
        : null,

      docurl: docUrl,
      is_locked: false,
      created_at: nowEpoch,
    });

    if (planError) throw planError;

    /* ---------- INSERT CP TARGETS ---------- */

    let cpIdsToInsert = [];

    if (cpScopeType === "SPECIFIC") {
      cpIdsToInsert = selectedCPs;
    } else {
      const { data: allCPs, error: allCPsError } = await supabase
        .from("CP")
        .select("cpId");

      if (allCPsError) throw allCPsError;

      cpIdsToInsert = (allCPs || []).map((cp) => cp.cpId);
    }

    if (cpIdsToInsert.length > 0) {
      const cpRows = cpIdsToInsert.map((cpId) => ({
        id: uuidv4(),
        aop_plan_id: aopPlanId,
        cp_id: cpId,
        created_at: nowEpoch,
      }));

      const { error: cpError } = await supabase
        .from("aop_cp_targets")
        .insert(cpRows);

      if (cpError) throw cpError;
    }

    /* ---------- SLABS ---------- */

    if (targetSlabs.length > 0) {
      const slabs = targetSlabs.map((slab, index) => ({
        id: uuidv4(),
        aop_plan_id: aopPlanId,
        from_value: Number(slab.from),
        to_value: slab.to ? Number(slab.to) : null,
        percentage:
          incentiveType === "percentage" ? Number(slab.incentive) : null,
        amount: incentiveType === "fixed" ? Number(slab.incentive) : null,
        sort_order: index + 1,
      }));

      const { error } = await supabase.from("aop_slabs").insert(slabs);

      if (error) throw error;
    }

    /* ---------- ELIGIBILITY ---------- */

    if (eligibility) {
      const { error } = await supabase.from("aop_eligibility_rules").insert({
        id: uuidv4(),
        aop_plan_id: aopPlanId,

        site_visit_enabled: eligibility.siteVisitEnabled || false,
        min_site_visits: eligibility.siteVisitMin
          ? Number(eligibility.siteVisitMin)
          : null,
        max_site_visits: eligibility.siteVisitMax
          ? Number(eligibility.siteVisitMax)
          : null,

        fos_enabled: eligibility.fosEnabled || false,
        min_fos_count: eligibility.fosMin ? Number(eligibility.fosMin) : null,
        max_fos_count: eligibility.fosMax ? Number(eligibility.fosMax) : null,

        created_at: nowEpoch,
        updated_at: nowEpoch,
      });

      if (error) throw error;
    }

    /* ---------- PROJECT SCOPE ---------- */

    const excludedProjectList = Array.isArray(excludedProjects)
      ? excludedProjects
      : excludedProjects
        ? [excludedProjects]
        : [];

    if (scopeType === "EXCLUDE" && excludedProjectList.length > 0) {
      const scopeRows = excludedProjectList.map((projectId) => ({
        id: uuidv4(),
        aop_plan_id: aopPlanId,
        project_id: projectId,
        created_at: nowEpoch,
      }));

      const { error } = await supabase
        .from("aop_scope_projects")
        .insert(scopeRows);

      if (error) throw error;
    }

    return { success: true, aopPlanId };
  } catch (error) {
    console.error("Create AOP Error:", error);
    return { success: false, error };
  }
};

const formatEpochDate = (epoch) => {
  if (!epoch) return null;
  return new Date(Number(epoch)).toLocaleDateString("en-GB", {
    day: "2-digit",
    month: "short",
    year: "numeric",
  });
};

export const getActivePopProjects = async () => {
  const today = new Date().toISOString().split("T")[0];

  const { data, error } = await supabase
    .from("pop_plans")
    .select(
      `
      start_date,
      end_date,
      status,
      pop_scope_entities (
        project_id,
        Projects (
          projectId,
          projectName
        )
      )
    `,
    )
    .in("status", ["Active", "Upcoming"]);

  if (error) throw error;

  return data.map((item) => ({
    projectId: item.pop_scope_entities[0]?.Projects?.projectId,
    projectName: item.pop_scope_entities[0]?.Projects?.projectName,
    startDate: item.start_date,
    endDate: item.end_date,
    status: item.status,
  }));
};

export const getAopPlans = async () => {
  const { data, error } = await supabase
    .from("aop_plans")
    .select(`
      id,
      financial_year,
      status,
      cp_scope_type,
      validity_start_date,
      validity_end_date,
      sign_off_start_date,
      sign_off_end_date,
      created_at,
      aop_slabs(id),
      aop_cp_targets(id),
      aop_scope_projects(
        project_id,
        Projects(
          projectName
        )
      )
    `)
    .order("created_at", { ascending: false });

  if (error) throw error;

  if (!data || data.length === 0) return [];

  // ✅ Get all AOP IDs
  const aopIds = data.map((row) => row.id);

  // ✅ Check which AOP plans are already used in CpBookingComputState
  const { data: usedRows, error: usedError } = await supabase
    .from("CpBookingComputState")
    .select("aop_plan_id")
    .in("aop_plan_id", aopIds);

  if (usedError) throw usedError;

  const usedAopIds = new Set(
    (usedRows || []).map((row) => row.aop_plan_id).filter(Boolean)
  );

  return data.map((row) => ({
    id: row.id,
    year: row.financial_year,
    status: row.status,
    slabs: row.aop_slabs?.length || 0,

    cpScope:
      row.cp_scope_type === "ALL"
        ? "All CPs"
        : `${row.aop_cp_targets?.length || 0} CPs selected`,

    // ✅ ADD THESE 2 RAW VALUES FOR UI
    validity_start_date: row.validity_start_date,
    validity_end_date: row.validity_end_date,

    // optional formatted validity string if you want to use directly
    validity:
      row.validity_start_date && row.validity_end_date
        ? `${new Date(Number(row.validity_start_date)).toLocaleDateString(
            "en-GB",
            {
              day: "2-digit",
              month: "short",
              year: "numeric",
            },
          )} - ${new Date(Number(row.validity_end_date)).toLocaleDateString(
            "en-GB",
            {
              day: "2-digit",
              month: "short",
              year: "numeric",
            },
          )}`
        : "Not Set",

    excludedProjects:
      row.aop_scope_projects?.map((p) => p.Projects?.projectName) || [],

    signOffWindow:
      row.sign_off_start_date && row.sign_off_end_date
        ? `${new Date(Number(row.sign_off_start_date)).toLocaleDateString(
            "en-GB",
            {
              day: "2-digit",
              month: "short",
              year: "numeric",
            },
          )} - ${new Date(Number(row.sign_off_end_date)).toLocaleDateString(
            "en-GB",
            {
              day: "2-digit",
              month: "short",
              year: "numeric",
            },
          )}`
        : "Not Required",

    canDelete: !usedAopIds.has(row.id),
    isUsedInBookingState: usedAopIds.has(row.id),
  }));
};

export const getPopPlans = async () => {
  const { data, error } = await supabase
    .from("pop_plans")
    .select(
      `
      id,
      name,
      scope_type,
      start_date,
      end_date,
      status,
        calculation_basis,
  reward_type,
      pop_scope_entities (
        project_id,
        wing_id,
        unit_id,
        Projects (
          projectName
        )
      ),
      pop_cp_targets ( id ),
        pop_slabs (
    id,
    from_value,
    to_value,
    percentage,
    amount,
    sort_order
  )
    `,
    )
    .is("deleted_at", null)
    .order("created_at", { ascending: false });

  if (error) throw error;

  return data.map((plan) => {
    const entity = plan.pop_scope_entities?.[0];

    return {
      id: plan.id,
      title: plan.name,
      project: entity?.Projects?.projectName || "-",

      type:
        plan.scope_type === "Project"
          ? "Project Based"
          : plan.scope_type === "Wing"
            ? "Wing Based"
            : "Unit Based",

      scope:
        plan.scope_type === "Project"
          ? "Entire Project"
          : plan.scope_type === "Wing"
            ? "Selected Wings"
            : "Selected Units",

      cps:
        plan.pop_cp_targets.length === 0
          ? "All CPs"
          : `${plan.pop_cp_targets.length} CPs selected`,

      validity: `${formatEpochDate(plan.start_date)} - ${formatEpochDate(plan.end_date)}`,

      active: plan.status === "Active",

      calculationBasis: plan.calculation_basis,
      rewardType: plan.reward_type,

      slabs: plan.pop_slabs || [],
    };
  });
};

export const getActivePopPlans = async () => {
  try {
    const { data, error } = await supabase
      .from("pop_plans")
      .select(
        `
        id,
        scope_type,
        pop_scope_entities (
          project_id,
          wing_id,
          unit_id
        )
      `,
      )
      .eq("status", "Active");

    if (error) throw error;

    const projectScopeMap = {};

    data.forEach((plan) => {
      plan.pop_scope_entities.forEach((entity) => {
        const projectId = entity.project_id;

        if (!projectScopeMap[projectId]) {
          projectScopeMap[projectId] = {
            projectScope: new Set(),
            wings: new Set(),
            units: new Set(),
          };
        }

        if (plan.scope_type === "Project") {
          projectScopeMap[projectId].projectScope.add(plan.id);
        }

        if (plan.scope_type === "Wing" && entity.wing_id) {
          projectScopeMap[projectId].wings.add(entity.wing_id);
        }

        if (plan.scope_type === "Unit" && entity.unit_id) {
          projectScopeMap[projectId].units.add(entity.unit_id);
        }
      });
    });

    return projectScopeMap;
  } catch (err) {
    console.error("Error fetching active POP plans:", err);
    return {};
  }
};

// Fetch units in active POP plans (those that are part of other active plans)
export const getUnitsInActivePopPlans = async () => {
  try {
    // Fetch all active POP plans and their scope entities (units)
    const { data, error } = await supabase
      .from("pop_plans")
      .select(
        `
        id,
        pop_scope_entities (
          unit_id
        )
      `,
      )
      .eq("status", "Active"); // Only active or upcoming POP plans

    if (error) throw error;

    // Extract unit IDs from the results and add them to a set for uniqueness
    const unitsInOtherPlans = new Set();

    data.forEach((plan) => {
      plan.pop_scope_entities.forEach((entity) => {
        if (entity.unit_id) {
          unitsInOtherPlans.add(entity.unit_id); // Add unit_id to the set
        }
      });
    });

    return Array.from(unitsInOtherPlans); // Return the set as an array of unit IDs
  } catch (error) {
    console.error("Error fetching units in active POP plans:", error);
    return [];
  }
};

/**
 * Fetch units in active POP plans for a given project and wings
 */
export const getUnitsInActivePopPlansForWings = async (projectId, wingIds) => {
  try {
    // Fetch units in active POP plans for selected wings and project
    const { data, error } = await supabase
      .from("pop_plans")
      .select(
        `
        pop_scope_entities (
          unit_id,
          wing_id
        )
      `,
      )
      .eq("status", "Active")
      .in("pop_scope_entities.wing_id", wingIds)
      .eq("pop_scope_entities.project_id", projectId);

    if (error) throw error;

    // Create a Set of unitIds that are part of active POP plans
    const unitsInActivePlans = new Set();
    data.forEach((plan) => {
      plan.pop_scope_entities.forEach((entity) => {
        if (entity.unit_id) {
          unitsInActivePlans.add(entity.unit_id);
        }
      });
    });

    return unitsInActivePlans;
  } catch (error) {
    console.error("Error fetching units in active POP plans for wings:", error);
    return new Set();
  }
};
export const getOverlappingPopScopes = async (
  projectId,
  startDate,
  endDate,
) => {
  try {
    if (!projectId || !startDate || !endDate) {
      return {
        projectBlocked: false,
        wings: new Set(),
        units: new Set(),
      };
    }

    const newStart = new Date(startDate).getTime();
    const newEnd = new Date(endDate).getTime();

    const { data, error } = await supabase
      .from("pop_plans")
      .select(
        `
        id,
        scope_type,
        start_date,
        end_date,
        pop_scope_entities!inner (
          project_id,
          wing_id,
          unit_id
        )
      `,
      )
      .eq("status", "Active")
      .eq("pop_scope_entities.project_id", projectId)
      .lte("start_date", newEnd)
      .gte("end_date", newStart);

    if (error) throw error;

    let projectBlocked = false;
    const wings = new Set();
    const units = new Set();

    if (!data || data.length === 0) {
      return { projectBlocked, wings, units };
    }

    data.forEach((plan) => {
      // 🔴 PROJECT LEVEL
      if (plan.scope_type === "Project") {
        projectBlocked = true;
      }

      plan.pop_scope_entities.forEach((entity) => {
        // 🔴 WING LEVEL PLAN → only add wing
        if (plan.scope_type === "Wing" && entity.wing_id) {
          wings.add(entity.wing_id);
        }

        // 🟢 UNIT LEVEL PLAN → only add unit
        if (plan.scope_type === "Unit" && entity.unit_id) {
          units.add(entity.unit_id);
        }
      });
    });

    return {
      projectBlocked,
      wings,
      units,
    };
  } catch (err) {
    console.error("Overlap Check Error:", err);
    return {
      projectBlocked: false,
      wings: new Set(),
      units: new Set(),
    };
  }
};


export const getOverlappingBaseBrokerage = async (
  projectId,
  startDate,
  endDate
) => {
  try {
    if (!projectId || !startDate || !endDate) {
      return {
        projectBlocked: false,
        wings: new Set(),
      };
    }

    const { data, error } = await supabase
      .from("base_brokerage_plan_projects")
      .select(`
        project_id,
        wing_id,
        base_brokerage_plans!inner (
          id,
          start_date,
          end_date,
          status
        )
      `)
      .eq("project_id", projectId)
      .eq("base_brokerage_plans.status", "Active")
      .lte("base_brokerage_plans.start_date", endDate)
      .gte("base_brokerage_plans.end_date", startDate);

    if (error) throw error;

    let projectBlocked = false;
    const wings = new Set();

    data?.forEach((row) => {
      // 🔴 PROJECT LEVEL (wing_id null)
      if (!row.wing_id) {
        projectBlocked = true;
      }

      // 🔴 WING LEVEL
      if (row.wing_id) {
        wings.add(row.wing_id);
      }
    });

    return { projectBlocked, wings };
  } catch (err) {
    console.error("Base Overlap Error:", err);
    return {
      projectBlocked: false,
      wings: new Set(),
    };
  }
};
export const checkDuplicateBasePlanName = async (planName) => {
  try {
    if (!planName?.trim()) return false;

    const { data, error } = await supabase
      .from("base_brokerage_plans")
      .select("id")
      .ilike("name", planName.trim())
      .is("deleted_at", null)
      .limit(1);

    if (error) throw error;

    return data?.length > 0;
  } catch (err) {
    console.error("Duplicate Base Plan Name Check Error:", err);
    return false;
  }
};
export const uploadPopDocument = async (file, popPlanId) => {
  try {
    if (!file) return { success: false, error: "No file selected" };
    if (!popPlanId) return { success: false, error: "Missing popPlanId" };

    const fileExt = file.name.split(".").pop();
    const filePath = `Sm_Uploaded_Docs/${popPlanId}.${fileExt}`;

    const { error: uploadError } = await supabase.storage
      .from("AOP_POP_Documents")
      .upload(filePath, file, { upsert: true });

    if (uploadError) throw uploadError;

    const { data } = supabase.storage
      .from("AOP_POP_Documents")
      .getPublicUrl(filePath);

    return { success: true, url: data.publicUrl };
  } catch (error) {
    console.error("Upload POP Document Error:", error);
    return { success: false, error };
  }
};

/**
 * Hard Delete POP Plan (Only if no payout transactions exist)
 */
export const deletePopPlan = async (popPlanId) => {
  try {
    if (!popPlanId) throw new Error("Missing popPlanId");

    /* 1️⃣ BLOCK IF PAYOUT TRANSACTION EXISTS */
    const { data: txnData, error: txnError } = await supabase
      .from("CpPayoutLedger")
      .select("CpPayoutLedgerId")
      .eq("pop_plan_id", popPlanId)
      .limit(1);

    if (txnError) throw txnError;

    if (txnData && txnData.length > 0) {
      return {
        success: false,
        blocked: true,
        message:
          "Cannot delete this POP plan because payout transactions exist.",
      };
    }

    /* 2️⃣ DELETE CHILD TABLES (MATCHING YOUR CREATE ORDER) */

    // 🔹 Delete CP targets
    const { error: cpError } = await supabase
      .from("pop_cp_targets")
      .delete()
      .eq("pop_plan_id", popPlanId);
    if (cpError) throw cpError;

    // 🔹 Delete eligibility
    const { error: eligibilityError } = await supabase
      .from("pop_eligibility_rules")
      .delete()
      .eq("pop_plan_id", popPlanId);
    if (eligibilityError) throw eligibilityError;

    // 🔹 Delete slabs
    const { error: slabError } = await supabase
      .from("pop_slabs")
      .delete()
      .eq("pop_plan_id", popPlanId);
    if (slabError) throw slabError;

    // 🔹 Delete scope entities
    const { error: scopeError } = await supabase
      .from("pop_scope_entities")
      .delete()
      .eq("pop_plan_id", popPlanId);
    if (scopeError) throw scopeError;

    /* 3️⃣ DELETE FILE FROM STORAGE (if exists) */

    const { data: planData } = await supabase
      .from("pop_plans")
      .select("docurl")
      .eq("id", popPlanId)
      .single();

    if (planData?.docurl) {
      const filePath = planData.docurl.split("/").pop();
      await supabase.storage
        .from("AOP_POP_Documents")
        .remove([`Sm_Uploaded_Docs/${filePath}`]);
    }

    /* 4️⃣ DELETE MAIN PLAN */

    const { error: popError } = await supabase
      .from("pop_plans")
      .delete()
      .eq("id", popPlanId);

    if (popError) throw popError;

    return { success: true };
  } catch (error) {
    console.error("Delete POP Plan Error:", error);
    return { success: false, error };
  }
};



export const deleteAopPlan = async (aopPlanId) => {
  try {
    if (!aopPlanId) throw new Error("Missing AOP ID");

    /* 1️⃣ BLOCK IF USED IN CpBookingComputState */
    const { data: bookingStateData, error: bookingStateError } = await supabase
      .from("CpBookingComputState")
      .select("CpBookingComputStateId")
      .eq("aop_plan_id", aopPlanId)
      .limit(1);

    if (bookingStateError) throw bookingStateError;

    if (bookingStateData && bookingStateData.length > 0) {
      return {
        success: false,
        blocked: true,
        message:
          "Cannot delete this AOP because it is already used in booking computation.",
      };
    }

    /* 2️⃣ BLOCK IF PAYOUT EXISTS */
    const { data: payoutData, error: payoutError } = await supabase
      .from("CpPayoutLedger")
      .select("CpPayoutLedgerId")
      .eq("aop_plan_id", aopPlanId)
      .limit(1);

    if (payoutError) throw payoutError;

    if (payoutData && payoutData.length > 0) {
      return {
        success: false,
        blocked: true,
        message: "Cannot delete this AOP because payout transactions exist.",
      };
    }

    /* 3️⃣ DELETE CHILD TABLES */
    await supabase
      .from("aop_eligibility_rules")
      .delete()
      .eq("aop_plan_id", aopPlanId);

    await supabase
      .from("aop_slabs")
      .delete()
      .eq("aop_plan_id", aopPlanId);

    await supabase
      .from("aop_cp_targets")
      .delete()
      .eq("aop_plan_id", aopPlanId);

    await supabase
      .from("aop_scope_projects")
      .delete()
      .eq("aop_plan_id", aopPlanId);

    /* 4️⃣ DELETE MAIN PLAN */
    const { error } = await supabase
      .from("aop_plans")
      .delete()
      .eq("id", aopPlanId);

    if (error) throw error;

    return { success: true };
  } catch (error) {
    console.error("Delete AOP Error:", error);
    return { success: false, error };
  }
};



export const getBaseBrokeragePlanDetails = async (planId) => {
  try {
    console.log("Fetching Base Plan Details for:", planId);

    if (!planId) {
      throw new Error("Missing planId");
    }

    const { data, error } = await supabase
      .from("base_brokerage_plans")
      .select(`
        *,
        base_brokerage_plan_projects(
          project_id,
          wing_id,
          Projects(projectName),
          Wings(wingName)
        )
      `)
      .eq("id", planId)
      .maybeSingle();

    if (error) throw error;

    return data || null;
  } catch (err) {
    console.error("Get Base Brokerage Plan Details Error:", err);
    return null;
  }
};




export const checkAopCpOverlap = async ({
  startDate,
  endDate,
  cpScopeType,
  selectedCPs = [],
}) => {
  try {
    if (!startDate || !endDate) {
      return {
        blocked: false,
        conflictingCPs: [],
        message: null,
      };
    }

    const newStart = new Date(startDate).getTime();
    const newEnd = new Date(endDate).getTime();

    // 1️⃣ Only plans whose validity period overlaps the new date range matter
    const { data: overlappingPlans, error } = await supabase
      .from("aop_plans")
      .select(`
        id,
        cp_scope_type,
        validity_start_date,
        validity_end_date
      `)
      .eq("status", "Active")
      .lte("validity_start_date", newEnd)
      .gte("validity_end_date", newStart);

    if (error) throw error;

    if (!overlappingPlans || overlappingPlans.length === 0) {
      return {
        blocked: false,
        conflictingCPs: [],
        message: null,
      };
    }

    const overlappingPlanIds = overlappingPlans.map((plan) => plan.id);

    // 2️⃣ Resolve the NEW plan's targeted CPs (specific selection, or every CP for "ALL")
    let newCpIds = selectedCPs;

    if (cpScopeType !== "SPECIFIC") {
      const { data: allCPs, error: allCPsError } = await supabase
        .from("CP")
        .select("cpId");

      if (allCPsError) throw allCPsError;

      newCpIds = (allCPs || []).map((cp) => cp.cpId);
    }

    // 3️⃣ Fetch targeted CPs for every overlapping plan (rows now exist for ALL-scope plans too)
    const { data: cpRows, error: cpError } = await supabase
      .from("aop_cp_targets")
      .select("aop_plan_id, cp_id")
      .in("aop_plan_id", overlappingPlanIds);

    if (cpError) throw cpError;

    const cpsByPlan = {};
    (cpRows || []).forEach((row) => {
      if (!cpsByPlan[row.aop_plan_id]) cpsByPlan[row.aop_plan_id] = [];
      cpsByPlan[row.aop_plan_id].push(row.cp_id);
    });

    // 4️⃣ A real conflict is a shared CP in an overlapping date range — project scope is ignored
    for (const plan of overlappingPlans) {
      const cpOverlap = (cpsByPlan[plan.id] || []).some((id) =>
        newCpIds.includes(id)
      );

      if (cpOverlap) {
        return {
          blocked: true,
          conflictingCPs: [],
          message:
            "This validity period overlaps with an existing AOP covering the same channel partner(s). Please adjust the dates or CP scope to continue.",
        };
      }
    }

    return {
      blocked: false,
      conflictingCPs: [],
      message: null,
    };
  } catch (error) {
    console.error("AOP CP Overlap Check Error:", error);
    return {
      blocked: false,
      conflictingCPs: [],
      message: null,
    };
  }
};


export const getBlockedAopCPs = async (startDate, endDate) => {
  try {
    if (!startDate || !endDate) {
      return {
        blockAll: false,
        blockedCPs: new Set(),
      };
    }

    const newStart = new Date(startDate).getTime();
    const newEnd = new Date(endDate).getTime();

    // 1️⃣ Get overlapping AOP plans (project scope is ignored — only date range matters)
    const { data: overlappingPlans, error } = await supabase
      .from("aop_plans")
      .select(`
        id,
        cp_scope_type,
        validity_start_date,
        validity_end_date,
        status
      `)
      .eq("status", "Active")
      .lte("validity_start_date", newEnd)
      .gte("validity_end_date", newStart);

    if (error) throw error;

    if (!overlappingPlans || overlappingPlans.length === 0) {
      return {
        blockAll: false,
        blockedCPs: new Set(),
      };
    }

    // 2️⃣ If any overlapping ALL CP plan exists → block all CPs
    const hasAllPlan = overlappingPlans.some(
      (plan) => plan.cp_scope_type === "ALL"
    );

    if (hasAllPlan) {
      return {
        blockAll: true,
        blockedCPs: new Set(),
      };
    }

    // 3️⃣ Otherwise fetch blocked CPs from overlapping SPECIFIC plans
    const overlappingPlanIds = overlappingPlans.map((plan) => plan.id);

    const { data: cpRows, error: cpError } = await supabase
      .from("aop_cp_targets")
      .select("cp_id")
      .in("aop_plan_id", overlappingPlanIds);

    if (cpError) throw cpError;

    return {
      blockAll: false,
      blockedCPs: new Set((cpRows || []).map((row) => row.cp_id)),
    };
  } catch (error) {
    console.error("Get Blocked AOP CPs Error:", error);
    return {
      blockAll: false,
      blockedCPs: new Set(),
    };
  }
};